/*jshint esversion: 6 */
/*global fetch, btoa */
import Q from 'q';
/**
 * 
 * @class TruckingAPI
 * @param {(string|object)} [domainOrOptions] - The project domain or options object. If object, see the object's optional properties.
 * @param {string} [domainOrOptions.domain] - The project domain
 * @param {object} [domainOrOptions.token] - auth token - object with value property and optional headerOrQueryName and isQuery properties
 */
let TruckingAPI = (function() {
    'use strict';

    function TruckingAPI(options) {
        let domain = (typeof options === 'object') ? options.domain : options;
        this.domain = domain ? domain : '';
        if (this.domain.length === 0) {
            throw new Error('Domain parameter must be specified as a string.');
        }
    }

    function serializeQueryParams(parameters) {
        let str = [];
        for (let p in parameters) {
            if (parameters.hasOwnProperty(p)) {
                str.push(encodeURIComponent(p) + '=' + encodeURIComponent(parameters[p]));
            }
        }
        return str.join('&');
    }

    function mergeQueryParams(parameters, queryParameters) {
        if (parameters.$queryParameters) {
            Object.keys(parameters.$queryParameters)
                .forEach(function(parameterName) {
                    let parameter = parameters.$queryParameters[parameterName];
                    queryParameters[parameterName] = parameter;
                });
        }
        return queryParameters;
    }

    /**
     * HTTP Request
     * @method
     * @name TruckingAPI#request
     * @param {string} method - http method
     * @param {string} url - url to do request
     * @param {object} parameters
     * @param {object} body - body parameters / object
     * @param {object} headers - header parameters
     * @param {object} queryParameters - querystring parameters
     * @param {object} form - form data object
     * @param {object} deferred - promise object
     */
    TruckingAPI.prototype.request = function(method, url, parameters, body, headers, queryParameters, form, deferred) {
        const queryParams = queryParameters && Object.keys(queryParameters).length ? serializeQueryParams(queryParameters) : null;
        const urlWithParams = url + (queryParams ? '?' + queryParams : '');

        if (body && !Object.keys(body).length) {
            body = undefined;
        }

        fetch(urlWithParams, {
            method,
            headers,
            credentials: 'include',
            body: JSON.stringify(body)
        }).then((response) => {
            deferred.resolve(response);
            return response;
        }).catch((error) => {
            deferred.reject(error);
        });
    };

    /**
     * Get the obfuscated credentials
     * @method
     * @name TruckingAPI#getCredentials
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.getCredentials = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/iam-creds';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * Get the json documentation of this entire api
     * @method
     * @name TruckingAPI#getDocs
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.getDocs = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/docs';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * get the version
     * @method
     * @name TruckingAPI#getVersion
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.getVersion = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/version';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * Generate a new invitation code.
     * @method
     * @name TruckingAPI#generateInvitationCode
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.generateInvitationCode = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/invitation-code';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * Get all the invitation codes
     * @method
     * @name TruckingAPI#getInvitationCodes
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.getInvitationCodes = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/invitation-codes';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * Authenticate a user and return tokens
     * @method
     * @name TruckingAPI#getToken
     * @param {object} parameters - method options and parameters
     * @param {} parameters.user - The user to authenticate
     */
    TruckingAPI.prototype.getToken = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/token';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['user'] !== undefined) {
            body = parameters['user'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('POST', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * Request a password reset
     * @method
     * @name TruckingAPI#requestPasswordReset
     * @param {object} parameters - method options and parameters
     * @param {} parameters.reset - The user to authenticate
     */
    TruckingAPI.prototype.requestPasswordReset = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/passwordreset';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['reset'] !== undefined) {
            body = parameters['reset'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('POST', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * Renew a token before expiration
     * @method
     * @name TruckingAPI#renewToken
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.renewToken = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/token/renew';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * Signup a user
     * @method
     * @name TruckingAPI#signup
     * @param {object} parameters - method options and parameters
     * @param {} parameters.user - The user to authenticate
     */
    TruckingAPI.prototype.signup = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/signup';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['user'] !== undefined) {
            body = parameters['user'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('POST', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * Signup a Customers
     * @method
     * @name TruckingAPI#signupCustomer
     * @param {object} parameters - method options and parameters
     * @param {} parameters.user - The user to authenticate
     */
    TruckingAPI.prototype.signupCustomer = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/customer/signup';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['user'] !== undefined) {
            body = parameters['user'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('POST', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#getUsers
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.getUsers = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/users';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * Delete multiple users
     * @method
     * @name TruckingAPI#deleteUsers
     * @param {object} parameters - method options and parameters
     * @param {} parameters.userIds - the list of userIds to delete
     */
    TruckingAPI.prototype.deleteUsers = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/users';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['userIds'] !== undefined) {
            body = parameters['userIds'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('DELETE', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * get the version
     * @method
     * @name TruckingAPI#login
     * @param {object} parameters - method options and parameters
     * @param {} parameters.user - The user to authenticate
     */
    TruckingAPI.prototype.login = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/login';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['user'] !== undefined) {
            body = parameters['user'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('POST', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * determine if a user is authenticated
     * @method
     * @name TruckingAPI#authenticated
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.authenticated = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/authenticated';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * get the version
     * @method
     * @name TruckingAPI#logout
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.logout = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/logout';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * create a new quote
     * @method
     * @name TruckingAPI#createQuote
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.orgId - 
     * @param {string} parameters.terminalId - 
     * @param {} parameters.quoteRequest - The user to authenticate
     */
    TruckingAPI.prototype.createQuote = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/organizations/{orgId}/quote/{terminalId}';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        path = path.replace('{orgId}', parameters['orgId']);

        if (parameters['orgId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: orgId'));
            return deferred.promise;
        }

        path = path.replace('{terminalId}', parameters['terminalId']);

        if (parameters['terminalId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: terminalId'));
            return deferred.promise;
        }

        if (parameters['quoteRequest'] !== undefined) {
            body = parameters['quoteRequest'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('POST', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * get did you mean
     * @method
     * @name TruckingAPI#getDidYouMean
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.address - 
     */
    TruckingAPI.prototype.getDidYouMean = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/did-you-mean';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['address'] !== undefined) {
            queryParameters['address'] = parameters['address'];
        }

        if (parameters['address'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: address'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * get the flat rate
     * @method
     * @name TruckingAPI#getPotentialDestinations
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.terminalId - 
     * @param {string} parameters.orgId - 
     * @param {string} parameters.city - 
     * @param {string} parameters.state - 
     * @param {integer} parameters.zip - 
     */
    TruckingAPI.prototype.getPotentialDestinations = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/destinations';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['terminalId'] !== undefined) {
            queryParameters['terminalId'] = parameters['terminalId'];
        }

        if (parameters['orgId'] !== undefined) {
            queryParameters['orgId'] = parameters['orgId'];
        }

        if (parameters['city'] !== undefined) {
            queryParameters['city'] = parameters['city'];
        }

        if (parameters['state'] !== undefined) {
            queryParameters['state'] = parameters['state'];
        }

        if (parameters['zip'] !== undefined) {
            queryParameters['zip'] = parameters['zip'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * get the terminals
     * @method
     * @name TruckingAPI#getTerminals
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.getTerminals = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/terminals';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * get a terminal
     * @method
     * @name TruckingAPI#getTerminal
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.terminalId - the user id of the user to get
     */
    TruckingAPI.prototype.getTerminal = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/terminals/{terminalId}';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        path = path.replace('{terminalId}', parameters['terminalId']);

        if (parameters['terminalId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: terminalId'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * get a terminal
     * @method
     * @name TruckingAPI#getAccessorial
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.terminalId - the user id of the user to get
     */
    TruckingAPI.prototype.getAccessorial = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/accessorial/{terminalId}';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        path = path.replace('{terminalId}', parameters['terminalId']);

        if (parameters['terminalId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: terminalId'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#updateAccessorial
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.terminalId - the terminal
     * @param {} parameters.query - 
     */
    TruckingAPI.prototype.updateAccessorial = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/accessorial/{terminalId}';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        path = path.replace('{terminalId}', parameters['terminalId']);

        if (parameters['terminalId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: terminalId'));
            return deferred.promise;
        }

        if (parameters['query'] !== undefined) {
            body = parameters['query'];
        }

        if (parameters['query'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: query'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('PATCH', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#resetPassword
     * @param {object} parameters - method options and parameters
     * @param {} parameters.reset - 
     */
    TruckingAPI.prototype.resetPassword = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/user/resetpassword';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['reset'] !== undefined) {
            body = parameters['reset'];
        }

        if (parameters['reset'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: reset'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('POST', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#getUser
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.userId - the user id of the user to get
     */
    TruckingAPI.prototype.getUser = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/users/{userId}';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        path = path.replace('{userId}', parameters['userId']);

        if (parameters['userId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: userId'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#deleteUser
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.userId - The user id of the user to delete
     */
    TruckingAPI.prototype.deleteUser = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/users/{userId}';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        path = path.replace('{userId}', parameters['userId']);

        if (parameters['userId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: userId'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('DELETE', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#getDistance
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.origins - 
     * @param {string} parameters.destinations - 
     */
    TruckingAPI.prototype.getDistance = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/distance';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['origins'] !== undefined) {
            queryParameters['origins'] = parameters['origins'];
        }

        if (parameters['origins'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: origins'));
            return deferred.promise;
        }

        if (parameters['destinations'] !== undefined) {
            queryParameters['destinations'] = parameters['destinations'];
        }

        if (parameters['destinations'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: destinations'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#getGeocode
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.address - The address
     */
    TruckingAPI.prototype.getGeocode = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/geocode';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['address'] !== undefined) {
            queryParameters['address'] = parameters['address'];
        }

        if (parameters['address'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: address'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#getSelf
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.getSelf = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/self';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#updateSelf
     * @param {object} parameters - method options and parameters
     * @param {file} parameters.avatar - the file to upload
     */
    TruckingAPI.prototype.updateSelf = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/self';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['multipart/form-data'];

        if (parameters['avatar'] !== undefined) {
            form['avatar'] = parameters['avatar'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('PATCH', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#getRateSheet
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.terminalId - the terminal
     * @param {string} parameters.orgId - the organization
     */
    TruckingAPI.prototype.getRateSheet = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/rates';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['terminalId'] !== undefined) {
            queryParameters['terminalId'] = parameters['terminalId'];
        }

        if (parameters['terminalId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: terminalId'));
            return deferred.promise;
        }

        if (parameters['orgId'] !== undefined) {
            queryParameters['orgId'] = parameters['orgId'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#updateRateSheet
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.terminalId - the terminal
     * @param {string} parameters.orgId - the organization
     * @param {} parameters.query - 
     */
    TruckingAPI.prototype.updateRateSheet = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/rates';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['terminalId'] !== undefined) {
            queryParameters['terminalId'] = parameters['terminalId'];
        }

        if (parameters['terminalId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: terminalId'));
            return deferred.promise;
        }

        if (parameters['orgId'] !== undefined) {
            queryParameters['orgId'] = parameters['orgId'];
        }

        if (parameters['query'] !== undefined) {
            body = parameters['query'];
        }

        if (parameters['query'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: query'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('PATCH', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#getOrganization
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.organizationId - ID of pet that needs to be fetched
     */
    TruckingAPI.prototype.getOrganization = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/organizations/{organizationId}';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        path = path.replace('{organizationId}', parameters['organizationId']);

        if (parameters['organizationId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: organizationId'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#getOrganizations
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.getOrganizations = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/organizations';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#addOrganization
     * @param {object} parameters - method options and parameters
     * @param {} parameters.user - The job to post
     */
    TruckingAPI.prototype.addOrganization = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/organizations';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['user'] !== undefined) {
            body = parameters['user'];
        }

        if (parameters['user'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: user'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('POST', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#createJobRequestV1
     * @param {object} parameters - method options and parameters
     * @param {array} parameters.files - 
     * @param {string} parameters.remarks - 
     * @param {string} parameters.quote - 
     */
    TruckingAPI.prototype.createJobRequestV1 = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/jobs-v1';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['multipart/form-data'];

        if (parameters['files'] !== undefined) {
            form['files'] = parameters['files'];
        }

        if (parameters['remarks'] !== undefined) {
            form['remarks'] = parameters['remarks'];
        }

        if (parameters['quote'] !== undefined) {
            form['quote'] = parameters['quote'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('POST', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#getJobs
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.getJobs = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/jobs';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#addJob
     * @param {object} parameters - method options and parameters
     * @param {} parameters.user - The job to post
     */
    TruckingAPI.prototype.addJob = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/jobs';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['user'] !== undefined) {
            body = parameters['user'];
        }

        if (parameters['user'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: user'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('POST', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#getJob
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.jobsId - ID of job that needs to be fetched
     */
    TruckingAPI.prototype.getJob = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/jobs/{jobsId}';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        path = path.replace('{jobsId}', parameters['jobsId']);

        if (parameters['jobsId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: jobsId'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#deleteJob
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.jobsId - ID of job that needs to be deleted
     */
    TruckingAPI.prototype.deleteJob = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/jobs/{jobsId}';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        path = path.replace('{jobsId}', parameters['jobsId']);

        if (parameters['jobsId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: jobsId'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('DELETE', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#updateJob
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.jobsId - ID of job that needs to be fetched
     * @param {} parameters.job - Update a job
     */
    TruckingAPI.prototype.updateJob = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/jobs/{jobsId}';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        path = path.replace('{jobsId}', parameters['jobsId']);

        if (parameters['jobsId'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: jobsId'));
            return deferred.promise;
        }

        if (parameters['job'] !== undefined) {
            body = parameters['job'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('PUT', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#getCapacityTerminals
     * @param {object} parameters - method options and parameters
     */
    TruckingAPI.prototype.getCapacityTerminals = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/capacity-terminals';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#updateCapacity
     * @param {object} parameters - method options and parameters
     * @param {} parameters.body - 
     */
    TruckingAPI.prototype.updateCapacity = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/capacity';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['body'] !== undefined) {
            body = parameters['body'];
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('PATCH', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };
    /**
     * 
     * @method
     * @name TruckingAPI#getCapacity
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.terminal - the terminal name
     * @param {string} parameters.start - the start date of the capcity
     * @param {string} parameters.end - the start date of the capcity
     */
    TruckingAPI.prototype.getCapacity = function(parameters) {
        if (parameters === undefined) {
            parameters = {};
        }
        let deferred = Q.defer();
        let domain = this.domain,
            path = '/capacity';
        let body = {},
            queryParameters = {},
            headers = {},
            form = {};

        headers['Accept'] = ['application/json'];
        headers['Content-Type'] = ['application/json'];

        if (parameters['terminal'] !== undefined) {
            queryParameters['terminal'] = parameters['terminal'];
        }

        if (parameters['start'] !== undefined) {
            queryParameters['start'] = parameters['start'];
        }

        if (parameters['start'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: start'));
            return deferred.promise;
        }

        if (parameters['end'] !== undefined) {
            queryParameters['end'] = parameters['end'];
        }

        if (parameters['end'] === undefined) {
            deferred.reject(new Error('Missing required  parameter: end'));
            return deferred.promise;
        }

        queryParameters = mergeQueryParams(parameters, queryParameters);

        this.request('GET', domain + path, parameters, body, headers, queryParameters, form, deferred);

        return deferred.promise;
    };

    return TruckingAPI;
})();

export default TruckingAPI;


// WEBPACK FOOTER //
// ./src/lib/TruckingReactAPI.js